import { CheckCircleIcon, ChevronDownIcon, ChevronRightIcon, LockClosedIcon } from "@heroicons/react/20/solid";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { differenceInCalendarMonths, parse } from "date-fns";
import { useId, useState } from "react";

import { useAuth } from "~/contexts/AuthContext";
import { UpcomingEventRows } from "~/modules/dashboard/components/UpcomingEventRows.tsx";
import { useDashboardCalendar } from "~/modules/dashboard/hooks/useDashboardCalendar.ts";
import { useDashboardUser } from "~/modules/dashboard/hooks/useDashboardUser.tsx";
import { useUpcomingEvents } from "~/modules/dashboard/hooks/useUpcomingEvents.ts";
import { monthlyClosingQueries } from "~/modules/timeTracking/api/monthlyClosing/monthlyClosingQueries.ts";
import { getMonthNameFromNumber } from "~/utils/dateAndTimeUtils.ts";

const VISIBLE_ROW_COUNT = 3;
const OLD_MONTH_AGE = 3;
const SECOND_MONTH_AGE = 2;
const OPEN_STATE_KEY = "dashboard-upcoming-open";

function readIsOpen() {
	try {
		return localStorage.getItem(OPEN_STATE_KEY) !== "false";
	} catch {
		return true;
	}
}

function writeIsOpen(isOpen: boolean) {
	try {
		localStorage.setItem(OPEN_STATE_KEY, String(isOpen));
	} catch {
		// The state is a convenience, so a blocked storage only loses it.
	}
}

function getTodoText(age: number) {
	if (age >= OLD_MONTH_AGE) return `Seit ${age} Monaten offen – bitte schließen`;
	return age === SECOND_MONTH_AGE ? "Monatsabschluss noch offen" : "Monatsabschluss offen";
}

/**
 * The full close of an earlier month is enforced only on the screen: the backend refuses no tracking
 * because an earlier month is open. So the text gets stricter the older the open month is.
 * The dashboard shows this section instead of `OpenEarlierMonthsReminder`, so one month never shows two
 * warnings on one page. The text addresses the user, so the section shows on the own dashboard only.
 * The section shows also when it is empty, so the user sees that nothing is due.
 */
export function UpcomingSection() {
	const user = useDashboardUser();
	const { user: authUser } = useAuth();
	const { goToMonth, setView, scrollToCalendar } = useDashboardCalendar();
	const [isExpanded, setIsExpanded] = useState(false);
	const [isOpen, setIsOpen] = useState(readIsOpen);
	const contentId = useId();
	const isOwnDashboard = !!user && authUser?.id === user.id;
	const { data } = useQuery({ ...monthlyClosingQueries.openMonths(user?.id ?? ""), enabled: isOwnDashboard });

	const events = useUpcomingEvents(isOwnDashboard);

	const today = new Date();
	// The read gives the newest month first. The oldest month is the most urgent, so it goes first.
	const openMonths = [...(data?.months ?? [])].reverse().map((key) => {
		const date = parse(key, "yyyy-MM", today);
		return { key, date, age: differenceInCalendarMonths(today, date) };
	});

	if (!isOwnDashboard) return null;

	const count = openMonths.length + events.length;

	const isCollapsed = openMonths.length > VISIBLE_ROW_COUNT && !isExpanded;
	const rows = isCollapsed ? openMonths.slice(0, VISIBLE_ROW_COUNT) : openMonths;
	const hiddenCount = openMonths.length - VISIBLE_ROW_COUNT;

	return (
		<section aria-label="Anstehende Termine und To-dos"
				 className="mt-8">
			<h3 className={isOpen ? "mb-3" : undefined}>
				<button type="button"
						className="flex min-h-7 w-full items-center gap-2 text-left text-lg font-semibold text-gray-900"
						aria-expanded={isOpen}
						aria-controls={contentId}
						onClick={() => {
							const next = !isOpen;
							writeIsOpen(next);
							setIsOpen(next);
						}}>
					Anstehende Termine und To-dos
					{count > 0 && (
						<span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary-700 text-xs font-semibold text-white">
							{count}
						</span>
					)}
					<ChevronDownIcon className={clsx("ml-auto size-5 shrink-0 text-gray-500", !isOpen && "-rotate-90")} />
				</button>
			</h3>
			<div id={contentId}
				 hidden={!isOpen}>
				{count === 0 && (
					<p className="flex min-h-11 items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
						<CheckCircleIcon className="size-4 shrink-0 text-gray-500" />
						Es steht gerade nichts an
					</p>
				)}
				{openMonths.length > 0 && (
					<ul className="divide-y divide-amber-200 overflow-hidden rounded-lg border border-amber-200 bg-amber-50/60">
						{rows.map((month) => {
							const isOld = month.age >= OLD_MONTH_AGE;
							return (
								<li key={month.key}>
									<button type="button"
											className="flex min-h-11 w-full items-center gap-3 px-3 py-2 text-left hover:bg-amber-100/60"
											onClick={() => {
												goToMonth(month.key);
												setView("month");
												// On a phone the calendar is far below this section.
												scrollToCalendar();
											}}>
										<LockClosedIcon className={clsx("size-4 shrink-0", isOld ? "text-red-600" : "text-amber-600")} />
										<span className="flex min-w-0 flex-1 flex-col text-sm sm:flex-row sm:gap-1">
											<span className="font-semibold text-gray-900">
												{getMonthNameFromNumber(month.date.getMonth())} {month.date.getFullYear()}
											</span>
											<span className={clsx(isOld ? "font-medium text-red-600" : "text-gray-700")}>
												<span className="hidden sm:inline">· </span>{getTodoText(month.age)}
											</span>
										</span>
										<span className="flex shrink-0 items-center text-xs font-medium text-primary-400">
											Zum Monat <ChevronRightIcon className="size-4" />
										</span>
									</button>
								</li>
							);
						})}
						{isCollapsed && (
							<li>
								<button type="button"
										className="flex min-h-11 w-full items-center px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-amber-100/60"
										onClick={() => setIsExpanded(true)}>
									{hiddenCount} weitere offene {hiddenCount === 1 ? "Monat" : "Monate"}
								</button>
							</li>
						)}
					</ul>
				)}
				{events.length > 0 && (
					<div className={openMonths.length > 0 ? "mt-3" : undefined}>
						<UpcomingEventRows events={events} />
					</div>
				)}
			</div>
		</section>
	);
}
